import React, { useState, useEffect, useRef } from 'react';
import ArticleImages from '../components/ArticleImages';
import { useParams } from 'react-router-dom';
import api from '../api';

const Spinner = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 20px',
    background: 'var(--accent-soft)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(59,130,246,0.2)', marginBottom: '16px' }}>
    <div style={{ width: '20px', height: '20px', border: '2px solid var(--accent)', borderTopColor: 'transparent',
      borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <span style={{ fontSize: '14px', color: 'var(--accent)' }}>{text}</span>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

export default function SiteContent() {
  const { id: siteId } = useParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [topic, setTopic] = useState('');
  const [keyword, setKeyword] = useState('');
  const [selectedModel, setSelectedModel] = useState('sonnet');
  const [wordCount, setWordCount] = useState(1500);
  const [preview, setPreview] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editMeta, setEditMeta] = useState({ title: '', description: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeAction, setActiveAction] = useState(null); // 'regenerating' | 'cleaning' | 'factchecking' | 'fixing' | 'saving'
  const [regenInstructions, setRegenInstructions] = useState('');
  const [showRegenInput, setShowRegenInput] = useState(false);
  const [factCheckResult, setFactCheckResult] = useState(null);
  const [cleanResult, setCleanResult] = useState(null);
  const editorRef = useRef(null);

  const models = {
    haiku: { name: 'Haiku 4.5', desc: 'זול ומהיר', icon: '⚡', color: '#22C55E' },
    sonnet: { name: 'Sonnet 4', desc: 'מומלץ', icon: '✨', color: '#3B82F6' },
    opus: { name: 'Opus 4', desc: 'פרימיום', icon: '👑', color: '#A855F7' },
  };

  const actionMessages = {
    regenerating: '🔄 משפר את המאמר עם AI... (20-40 שניות)',
    cleaning: '🧹 מנקה סגירי AI מהתוכן... (20-40 שניות)',
    factchecking: '🔍 בודק עובדות ודיוק... (15-30 שניות)',
    fixing: '🔧 מתקן בעיות שזוהו... (20-40 שניות)',
    saving: '💾 שומר שינויים...',
  };

  useEffect(() => { loadArticles(); }, [siteId]);

  const loadArticles = async () => {
    try { setArticles(await api.getArticles(siteId)); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    if (!topic.trim() || !keyword.trim()) { setError('נדרש נושא ומילת מפתח'); return; }
    setError(''); setGenerating(true); setSuccess('');
    try {
      const data = await api.generateArticle(siteId, topic, keyword, selectedModel, wordCount);
      setPreview(data); setEditContent(data.content);
      setEditMeta({ title: data.meta_title, description: data.meta_description });
      setShowGenerator(false); loadArticles();
      setSuccess(`מאמר נוצר! (${data.generation_meta?.output_tokens} tokens, $${data.generation_meta?.cost?.toFixed(4)})`);
    } catch (e) { setError('שגיאה ביצירת מאמר: ' + e.message); }
    finally { setGenerating(false); }
  };

  const handleRegenerate = async () => {
    if (!preview) return;
    setActiveAction('regenerating'); setError(''); setCleanResult(null);
    try {
      const data = await api.regenerateArticle(preview.article_id, regenInstructions);
      setPreview(p => ({ ...p, content: data.content, meta_title: data.meta_title, meta_description: data.meta_description, generation_meta: data.generation_meta, changes_made: data.changes_made }));
      setEditContent(data.content);
      setEditMeta({ title: data.meta_title, description: data.meta_description });
      setShowRegenInput(false); setRegenInstructions('');
      setSuccess(`שופר! שינויים: ${(data.changes_made || []).join(', ')} ($${data.generation_meta?.cost?.toFixed(4)})`);
    } catch (e) { setError('שגיאה: ' + e.message); }
    finally { setActiveAction(null); }
  };

  const handleClean = async () => {
    if (!preview) return;
    setActiveAction('cleaning'); setError(''); setCleanResult(null);
    try {
      const data = await api.cleanArticle(preview.article_id);
      setPreview(p => ({ ...p, content: data.content }));
      setEditContent(data.content);
      setCleanResult(data);
      setSuccess(`נוקה! ${data.total_changes || 0} שינויים ($${data.cost?.toFixed(4)})`);
    } catch (e) { setError('שגיאה: ' + e.message); }
    finally { setActiveAction(null); }
  };

  const handleFactCheck = async () => {
    if (!preview) return;
    setActiveAction('factchecking'); setError(''); setFactCheckResult(null);
    try {
      const data = await api.factCheckArticle(preview.article_id);
      setFactCheckResult(data);
    } catch (e) { setError('שגיאה: ' + e.message); }
    finally { setActiveAction(null); }
  };

  const handleFixFacts = async () => {
    if (!preview || !factCheckResult?.issues?.length) return;
    setActiveAction('fixing'); setError('');
    try {
      const data = await api.fixFacts(preview.article_id, factCheckResult.issues);
      setPreview(p => ({ ...p, content: data.content }));
      setEditContent(data.content);
      setFactCheckResult(null);
      setSuccess(`תוקן! ${(data.fixes_applied || []).join(' | ')} ($${data.cost?.toFixed(4)})`);
    } catch (e) { setError('שגיאה: ' + e.message); }
    finally { setActiveAction(null); }
  };

  const handleSaveEdit = async () => {
    if (!preview) return;
    setActiveAction('saving'); setError('');
    try {
      const contentToSave = editorRef.current ? editorRef.current.getContent() : editContent;
      await api.updateArticle(preview.article_id, { content: contentToSave, meta_title: editMeta.title, meta_description: editMeta.description });
      setPreview(p => ({ ...p, content: contentToSave, meta_title: editMeta.title, meta_description: editMeta.description }));
      setEditContent(contentToSave); setEditMode(false);
      setSuccess('נשמר בהצלחה!');
    } catch (e) { setError('שגיאה בשמירה: ' + e.message); }
    finally { setActiveAction(null); }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('למחוק את המאמר?')) return;
    try {
      await api.deleteArticle(articleId);
      if (preview?.article_id === articleId) { setPreview(null); setFactCheckResult(null); setCleanResult(null); }
      loadArticles();
    } catch (e) { setError('שגיאה: ' + e.message); }
  };

  const handleViewArticle = async (articleId) => {
    try {
      const data = await api.getArticle(articleId);
      setPreview({ article_id: data.id, meta_title: data.meta_title, meta_description: data.meta_description, content: data.content, internal_links: data.internal_links || [], faq: [], images_data: data.images_data || null });
      setEditContent(data.content); setEditMeta({ title: data.meta_title, description: data.meta_description });
      setShowGenerator(false); setFactCheckResult(null); setCleanResult(null); setEditMode(false);
    } catch (e) { setError('שגיאה: ' + e.message); }
  };

  const statusMap = { draft: { color: '#F59E0B', label: 'טיוטה' }, review: { color: '#3B82F6', label: 'בבדיקה' }, published: { color: '#22C55E', label: 'פורסם' } };

  // TinyMCE
  const [editorLoaded, setEditorLoaded] = useState(false);
  useEffect(() => {
    if (editMode && !editorLoaded && !document.getElementById('tinymce-script')) {
      const s = document.createElement('script'); s.id = 'tinymce-script';
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.2/tinymce.min.js';
      s.onload = () => setEditorLoaded(true); document.head.appendChild(s);
    } else if (editMode && window.tinymce) setEditorLoaded(true);
  }, [editMode]);

  useEffect(() => {
    if (editMode && editorLoaded && window.tinymce) {
      window.tinymce.remove('#article-editor');
      window.tinymce.init({
        selector: '#article-editor', directionality: 'rtl', height: 500,
        menubar: 'edit insert format', plugins: 'lists link image code table wordcount',
        toolbar: 'undo redo | blocks | bold italic underline | bullist numlist | link image | table | code | wordcount',
        content_style: "body{font-family:'Heebo',Arial,sans-serif;font-size:15px;line-height:1.8;direction:rtl;color:#e2e8f0;background:#111827;padding:16px}h2{font-size:22px;margin-top:24px;color:#f1f5f9}h3{font-size:18px;margin-top:20px}a{color:#60a5fa}p{margin-bottom:12px}",
        skin: 'oxide-dark', content_css: 'dark',
        setup: (ed) => { editorRef.current = ed; ed.on('init', () => ed.setContent(editContent)); },
      });
    }
    return () => { if (window.tinymce) window.tinymce.remove('#article-editor'); };
  }, [editMode, editorLoaded]);

  const isActionActive = !!activeAction;
  const st = {
    input: { width: '100%', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', fontSize: '14px', outline: 'none' },
    btn: (primary, small) => ({
      padding: small ? '6px 12px' : '8px 16px', borderRadius: 'var(--radius-sm)', border: primary ? 'none' : '1px solid var(--border)',
      background: primary ? 'var(--accent)' : 'var(--surface-hover)', color: primary ? '#fff' : 'var(--text)',
      cursor: isActionActive ? 'not-allowed' : 'pointer', fontSize: small ? '12px' : '13px', fontWeight: 500,
      opacity: isActionActive ? 0.6 : 1, transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px',
    }),
    card: { background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden' },
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>כתיבת תוכן</div>
          <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '2px' }}>יצירה, עריכה, בדיקת עובדות וניקוי סגירי AI</div>
        </div>
        <button style={st.btn(true)} onClick={() => { setShowGenerator(true); setPreview(null); setFactCheckResult(null); setCleanResult(null); setEditMode(false); }}>
          ✨ מאמר חדש
        </button>
      </div>

      {error && <div style={{ padding: '10px 16px', borderRadius: 'var(--radius-sm)', marginBottom: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: '13px', display: 'flex', justifyContent: 'space-between' }}>
        {error}<span onClick={() => setError('')} style={{ cursor: 'pointer', paddingRight: '8px' }}>✕</span>
      </div>}
      {success && <div style={{ padding: '10px 16px', borderRadius: 'var(--radius-sm)', marginBottom: '12px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#86efac', fontSize: '13px', display: 'flex', justifyContent: 'space-between' }}>
        {success}<span onClick={() => setSuccess('')} style={{ cursor: 'pointer', paddingRight: '8px' }}>✕</span>
      </div>}

      <div style={{ display: 'grid', gridTemplateColumns: preview || showGenerator ? '300px 1fr' : '1fr', gap: '20px', alignItems: 'start' }}>
        {/* List */}
        <div style={st.card}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: '15px' }}>מאמרים ({articles.length})</div>
          {loading ? <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>טוען...</div> :
           articles.length === 0 ? <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>אין מאמרים עדיין</div> :
           articles.map(a => {
             const st2 = statusMap[a.status] || { color: '#666', label: a.status };
             return (
              <div key={a.id} onClick={() => handleViewArticle(a.id)}
                style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                  background: preview?.article_id === a.id ? 'var(--surface-active)' : 'transparent', transition: 'background 0.15s' }}
                onMouseEnter={e => { if (preview?.article_id !== a.id) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                onMouseLeave={e => { if (preview?.article_id !== a.id) e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 500, fontSize: '13px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title || 'ללא כותרת'}</span>
                  <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '8px', background: st2.color + '20', color: st2.color, flexShrink: 0 }}>{st2.label}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)' }}>
                  <span>{a.created_at ? new Date(a.created_at).toLocaleDateString('he-IL') : ''}</span>
                  <span onClick={e => { e.stopPropagation(); handleDelete(a.id); }} style={{ cursor: 'pointer', color: 'var(--red)', opacity: 0.4 }}>🗑</span>
                </div>
              </div>
           );})}
        </div>

        {/* Main area */}
        {(showGenerator || preview) && <div>

          {/* Generator */}
          {showGenerator && !preview && <div style={st.card}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>✨ יצירת מאמר חדש</div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '13px', color: 'var(--text-secondary)' }}>נושא</label>
                <input style={st.input} value={topic} onChange={e => setTopic(e.target.value)} placeholder='לדוגמה: "שירותי גינון מקצועיים"' />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '13px', color: 'var(--text-secondary)' }}>מילת מפתח</label>
                <input style={st.input} value={keyword} onChange={e => setKeyword(e.target.value)} placeholder='לדוגמה: "גינון מקצועי"' />
              </div>

              {/* Models */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '13px', color: 'var(--text-secondary)' }}>מודל</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {Object.entries(models).map(([k, m]) => (
                    <button key={k} onClick={() => setSelectedModel(k)} style={{
                      flex: 1, padding: '8px 6px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'center',
                      border: selectedModel === k ? `2px solid ${m.color}` : '1px solid var(--border)',
                      background: selectedModel === k ? m.color + '15' : 'var(--bg)', color: selectedModel === k ? m.color : 'var(--text-secondary)', transition: 'all 0.15s',
                    }}>
                      <div style={{ fontSize: '15px' }}>{m.icon}</div>
                      <div style={{ fontSize: '12px', fontWeight: 600 }}>{m.name}</div>
                      <div style={{ fontSize: '10px', opacity: 0.8 }}>{m.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Word count */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '13px', color: 'var(--text-secondary)' }}>
                  מילים: <strong style={{ color: 'var(--text)' }}>{wordCount.toLocaleString()}</strong>
                </label>
                <input type="range" min="500" max="4000" step="250" value={wordCount} onChange={e => setWordCount(Number(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--accent)' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  <span>500</span><span>1,500</span><span>2,500</span><span>4,000</span>
                </div>
              </div>

              {generating ? <Spinner text={`יוצר מאמר עם ${models[selectedModel].name}... (30-60 שניות)`} /> :
                <button style={{ ...st.btn(true), width: '100%', padding: '12px', justifyContent: 'center', fontSize: '14px' }} onClick={handleGenerate}>
                  ✨ צור מאמר ({wordCount.toLocaleString()} מילים)
                </button>
              }
            </div>
          </div>}

          {/* Preview */}
          {preview && <div style={st.card}>
            {/* Header */}
            <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontWeight: 600, fontSize: '15px' }}>תצוגה מקדימה</span>
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                {!editMode ? <>
                  <button style={st.btn(false, true)} onClick={() => setEditMode(true)} disabled={isActionActive}>✏️ עריכה</button>
                  <button style={st.btn(false, true)} onClick={() => setShowRegenInput(!showRegenInput)} disabled={isActionActive}>🔄 שפר</button>
                  <button style={st.btn(false, true)} onClick={handleClean} disabled={isActionActive}>🧹 נקה AI</button>
                  <button style={st.btn(false, true)} onClick={handleFactCheck} disabled={isActionActive}>🔍 עובדות</button>

                </> : <>
                  <button style={st.btn(true, true)} onClick={handleSaveEdit} disabled={isActionActive}>💾 שמור</button>
                  <button style={st.btn(false, true)} onClick={() => setEditMode(false)}>ביטול</button>
                </>}
              </div>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Loading indicator */}
              {activeAction && <Spinner text={actionMessages[activeAction]} />}

              {/* Regen input */}
              {showRegenInput && !activeAction && <div style={{ marginBottom: '14px', padding: '12px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                <input style={{ ...st.input, marginBottom: '8px' }} value={regenInstructions} onChange={e => setRegenInstructions(e.target.value)}
                  placeholder='הנחיות (לדוגמה: "הוסף דוגמאות", "קצר הפתיחה")' />
                <button style={st.btn(true, true)} onClick={handleRegenerate}>🔄 שפר</button>
              </div>}

              {/* Changes made (from regeneration) */}
              {preview.changes_made?.length > 0 && <div style={{ marginBottom: '14px', padding: '10px 14px', background: 'var(--green-soft)', borderRadius: 'var(--radius-sm)', fontSize: '13px' }}>
                <strong>שיפורים:</strong> {preview.changes_made.join(' | ')}
              </div>}

              {/* Clean results */}
              {cleanResult && cleanResult.changes?.length > 0 && <div style={{ marginBottom: '14px', padding: '12px 14px', background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: 'var(--radius-sm)', fontSize: '13px' }}>
                <div style={{ fontWeight: 600, marginBottom: '6px', color: 'var(--purple)' }}>🧹 {cleanResult.total_changes} שינויים בוצעו:</div>
                {cleanResult.changes.slice(0, 8).map((c, i) => (
                  <div key={i} style={{ padding: '3px 0', color: c.type === 'removed' ? '#f87171' : '#86efac', fontSize: '12px' }}>
                    {c.type === 'removed' ? '−' : '+'} {c.text}
                  </div>
                ))}
                {cleanResult.changes.length > 8 && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>ועוד {cleanResult.changes.length - 8} שינויים...</div>}
                <button onClick={() => setCleanResult(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '11px', marginTop: '4px' }}>סגור ✕</button>
              </div>}

              {/* SEO Preview */}
              <div style={{ marginBottom: '18px', padding: '14px 16px', background: '#0a0a0a', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '10px', color: '#70757a', marginBottom: '5px' }}>Google Preview</div>
                {editMode ? <>
                  <input value={editMeta.title} onChange={e => setEditMeta(p => ({ ...p, title: e.target.value }))}
                    style={{ ...st.input, color: '#8ab4f8', fontSize: '17px', marginBottom: '5px', background: 'transparent', border: '1px solid #333' }} />
                  <input value={editMeta.description} onChange={e => setEditMeta(p => ({ ...p, description: e.target.value }))}
                    style={{ ...st.input, color: '#bdc1c6', fontSize: '13px', background: 'transparent', border: '1px solid #333' }} />
                </> : <>
                  <div style={{ color: '#8ab4f8', fontSize: '17px', marginBottom: '3px', lineHeight: 1.3 }}>{preview.meta_title}</div>
                  <div style={{ color: '#bdc1c6', fontSize: '13px', lineHeight: 1.5 }}>{preview.meta_description}</div>
                </>}
                <div style={{ fontSize: '10px', color: '#555', marginTop: '6px' }}>
                  Title: {(editMode ? editMeta.title : preview.meta_title || '').length}/60 | Desc: {(editMode ? editMeta.description : preview.meta_description || '').length}/155
                </div>
              </div>

              {/* Meta */}
              {preview.generation_meta && <div style={{ display: 'flex', gap: '14px', marginBottom: '14px', fontSize: '12px', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                <span>💰 ${preview.generation_meta.cost?.toFixed(4)}</span>
                <span>📊 {preview.generation_meta.input_tokens}↑ {preview.generation_meta.output_tokens}↓</span>
                {preview.word_count_estimate && <span>📝 ~{preview.word_count_estimate} מילים</span>}
                {preview.faq?.length > 0 && <span>❓ {preview.faq.length} FAQ</span>}
                {preview.internal_links?.length > 0 && <span>🔗 {preview.internal_links.length} קישורים</span>}
              </div>}

              {/* Fact check */}
              {factCheckResult && <div style={{ marginBottom: '14px', padding: '14px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 600, fontSize: '14px' }}>🔍 בדיקת עובדות</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 700, color: factCheckResult.overall_score >= 7 ? 'var(--green)' : factCheckResult.overall_score >= 4 ? 'var(--orange)' : 'var(--red)' }}>
                      {factCheckResult.overall_score}/10
                    </span>
                    {factCheckResult.issues?.length > 0 && (
                      <button style={st.btn(true, true)} onClick={handleFixFacts} disabled={isActionActive}>🔧 תקן הכל</button>
                    )}
                    <button onClick={() => setFactCheckResult(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                  </div>
                </div>
                {factCheckResult.summary && <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>{factCheckResult.summary}</div>}
                {factCheckResult.issues?.map((issue, i) => (
                  <div key={i} style={{ padding: '8px 10px', marginBottom: '5px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', fontSize: '13px',
                    borderRight: `3px solid ${issue.severity === 'high' ? 'var(--red)' : issue.severity === 'medium' ? 'var(--orange)' : 'var(--text-muted)'}` }}>
                    <div>{issue.issue}</div>
                    {issue.suggestion && <div style={{ color: 'var(--green)', marginTop: '3px', fontSize: '12px' }}>💡 {issue.suggestion}</div>}
                  </div>
                ))}
                {factCheckResult.cost && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>עלות: ${factCheckResult.cost.toFixed(4)}</div>}
              </div>}

              {/* Content */}
              {editMode ? (
                <textarea id="article-editor" defaultValue={editContent} style={{ width: '100%', minHeight: '500px', padding: '16px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', fontSize: '14px', lineHeight: 1.6, fontFamily: "'Heebo',sans-serif", direction: 'rtl', resize: 'vertical' }} />
              ) : (
                <div style={{ padding: '20px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', lineHeight: 1.8, fontSize: '15px', direction: 'rtl', maxHeight: '600px', overflow: 'auto' }}
                  dangerouslySetInnerHTML={{ __html: preview.content }} />
              )}

              {/* Internal links */}
              {preview.internal_links?.length > 0 && !editMode && <div style={{ marginTop: '14px', padding: '14px 16px', background: 'var(--accent-soft)', borderRadius: 'var(--radius-sm)' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>🔗 קישורים פנימיים מומלצים</div>
                {preview.internal_links.map((l, i) => (
                  <div key={i} style={{ fontSize: '13px', marginBottom: '6px', display: 'flex', gap: '6px', alignItems: 'baseline' }}>
                    <span style={{ color: 'var(--accent)', fontWeight: 500 }}>{l.anchor_text}</span>
                    <span style={{ color: 'var(--text-muted)' }}>→</span>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{l.url?.replace(/^https?:\/\//, '').substring(0, 40)}</a>
                    {l.context && <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>({l.context})</span>}
                  </div>
                ))}
              </div>}

              {/* FAQ */}
              {preview.faq?.length > 0 && !editMode && <div style={{ marginTop: '14px', padding: '14px 16px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-sm)' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>❓ שאלות ({preview.faq.length})</div>
                {preview.faq.map((q, i) => (
                  <div key={i} style={{ marginBottom: '10px', paddingBottom: '8px', borderBottom: i < preview.faq.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ fontWeight: 500, fontSize: '14px' }}>{q.question}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '3px' }}>{q.answer}</div>
                  </div>
                ))}
              </div>}

              {/* Images */}
              {!editMode && (
                <ArticleImages
                  articleId={preview.article_id}
                  siteId={siteId}
                  initialImages={preview.images_data}
                  onPublished={(data) => {
                    setSuccess(`המאמר הועלה ל-WordPress עם ${data.images_uploaded} תמונות`);
                    loadArticles();
                  }}
                />
              )}
            </div>
          </div>}
        </div>}
      </div>
    </div>
  );
}
